import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { AdminLayout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Timer, Download, DollarSign, ArrowLeft, Calendar } from "lucide-react";
import { toast } from "sonner";

const fmtMin = (m = 0) => {
  const h = Math.floor(m / 60), mm = m % 60;
  return `${h}h ${mm}m`;
};

const fmtHours = (m = 0) => {
  return (m / 60).toFixed(2);
};

export default function AdminPayroll() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [week, setWeek] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [hourlyRate, setHourlyRate] = useState(10.0);
  const [downloadingPayStub, setDownloadingPayStub] = useState(false);

  const loadHoursBreakdown = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append("period", period);
      if (year) params.append("year", year);
      if (period === "monthly" && month) params.append("month", month);
      if (period === "weekly" && week) params.append("week", week);
      if (selectedUser && selectedUser !== "all") params.append("user_id", selectedUser);
      
      const r = await api.get(`/admin/payroll/hours-breakdown?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      console.error("Failed to load hours breakdown", e);
      setError(e?.response?.data?.detail || "Failed to load hours breakdown");
      toast.error("Failed to load hours breakdown");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHoursBreakdown();
  }, [period, year, month, week, selectedUser]);

  const downloadPayStub = async (userId, userName) => {
    setDownloadingPayStub(true);
    try {
      const params = new URLSearchParams();
      params.append("period", period);
      params.append("user_id", userId);
      params.append("hourly_rate", hourlyRate);
      if (year) params.append("year", year);
      if (period === "monthly" && month) params.append("month", month);
      if (period === "weekly" && week) params.append("week", week);
      
      const r = await api.get(`/admin/payroll/pay-stub-csv?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pay_stub_${userName}_${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Pay stub downloaded");
    } catch (e) {
      console.error("Failed to download pay stub", e);
      toast.error("Failed to download pay stub");
    } finally {
      setDownloadingPayStub(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        <header className="flex items-end justify-between">
          <div>
            <div className="label-overline text-[#D4AF37]">Admin payroll</div>
            <h1 className="font-display text-4xl mt-2">Hours & Pay Stubs</h1>
          </div>
          <Button onClick={() => navigate("/admin")} variant="outline" className="border-[#21362A] text-[#A1B4A8]">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </header>

        <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
          <CardHeader>
            <CardTitle className="font-display text-xl">Filter Options</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-5 gap-4">
            <div>
              <label className="label-overline text-xs mb-2 block">Period</label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="bg-[#132018] border-[#21362A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#132018] border-[#21362A]">
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-overline text-xs mb-2 block">Year</label>
              <Input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="bg-[#132018] border-[#21362A] text-white"
              />
            </div>
            {period === "monthly" && (
              <div>
                <label className="label-overline text-xs mb-2 block">Month</label>
                <Select value={month?.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                  <SelectTrigger className="bg-[#132018] border-[#21362A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#132018] border-[#21362A]">
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <SelectItem key={m} value={m.toString()}>{new Date(year, m-1).toLocaleString('default', { month: 'long' })}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {period === "weekly" && (
              <div>
                <label className="label-overline text-xs mb-2 block">Week Number</label>
                <Input
                  type="number"
                  value={week || ""}
                  onChange={(e) => setWeek(e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Current week"
                  className="bg-[#132018] border-[#21362A] text-white"
                />
              </div>
            )}
            <div>
              <label className="label-overline text-xs mb-2 block">Worker (Optional)</label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="bg-[#132018] border-[#21362A] text-white">
                  <SelectValue placeholder="All workers" />
                </SelectTrigger>
                <SelectContent className="bg-[#132018] border-[#21362A]">
                  <SelectItem value="all">All workers</SelectItem>
                  {data?.report?.map((w) => (
                    <SelectItem key={w.user_id} value={w.user_id}>{w.user_name || w.user_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12 text-[#A1B4A8]">Loading hours breakdown...</div>
        ) : error ? (
          <div className="text-center py-12 text-[#E63946]">{error}</div>
        ) : data ? (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Kpi icon={Timer} label="Total Workers" value={data.report.length} />
              <Kpi icon={Timer} label="Total Work Hours" value={data.report.reduce((sum, w) => sum + w.total_work_minutes, 0) / 60} />
              <Kpi icon={DollarSign} label="Hourly Rate" value={`$${hourlyRate.toFixed(2)}`} />
              <Kpi icon={Calendar} label="Period" value={period} />
            </section>

            <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
              <CardHeader>
                <CardTitle className="font-display text-2xl">Hours Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#21362A]">
                        <th className="text-left py-3 px-4 text-[#A1B4A8] font-medium">Worker</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Total Shift</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Work Hours</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Break Hours</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Est. Earnings</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.report.map((worker) => (
                        <tr key={worker.user_id} className="border-b border-[#21362A]/50 hover:bg-[#132018]/50">
                          <td className="py-3 px-4 font-medium text-white">{worker.user_name || worker.user_id}</td>
                          <td className="text-right py-3 px-4 text-[#A1B4A8]">{fmtHours(worker.total_shift_minutes)}h</td>
                          <td className="text-right py-3 px-4 text-[#D4AF37]">{fmtHours(worker.total_work_minutes)}h</td>
                          <td className="text-right py-3 px-4 text-[#A1B4A8]">{fmtHours(worker.total_break_minutes)}h</td>
                          <td className="text-right py-3 px-4 text-[#097969]">${((worker.total_work_minutes / 60) * hourlyRate).toFixed(2)}</td>
                          <td className="text-right py-3 px-4">
                            <Button
                              onClick={() => downloadPayStub(worker.user_id, worker.user_name || worker.user_id)}
                              disabled={downloadingPayStub}
                              size="sm"
                              className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07]"
                            >
                              <Download className="w-4 h-4 mr-1" /> Pay Stub
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.report.length === 0 && (
                  <div className="text-center py-8 text-[#A1B4A8]">No hours data for the selected period</div>
                )}
              </CardContent>
            </Card>

            {data.report.length > 0 && (
              <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
                <CardHeader>
                  <CardTitle className="font-display text-xl">Hourly Rate for Pay Stubs</CardTitle>
                </CardHeader>
                <CardContent className="flex items-center gap-4">
                  <label className="label-overline text-xs">Set hourly rate ($):</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                    className="bg-[#132018] border-[#21362A] text-white w-32"
                  />
                  <span className="text-sm text-[#A1B4A8]">This rate will be used for pay stub calculations</span>
                </CardContent>
              </Card>
            )}
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}

function Kpi({ icon: Icon, label, value, tone = "default" }) {
  const toneColors = {
    default: "text-[#D4AF37]",
    danger: "text-[#E63946]",
    success: "text-[#097969]",
  };
  return (
    <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${toneColors[tone]}`} />
          <div>
            <div className="label-overline text-xs">{label}</div>
            <div className="font-display text-2xl mt-1">{value}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
